import React, { useState, useEffect } from 'react';
import { Calendar, Users, MapPin, Clock, Heart, User, Euro, Eye, EyeOff } from 'lucide-react';
import { eventApi, statisticsApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import type { Tables } from '../lib/types';
import { withCache } from '../lib/cache';

interface EventWithLocation extends Tables<'events'> {
  locationImage?: string | null;
  locationImagePositionX?: number;
  locationImagePositionY?: number;
}

const Homepage = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({
    events: 0,
    participants: 0,
    locations: 0,
    matches: 0,
    contacts: 0,
    totalRevenue: 0
  });
  const [upcomingEvents, setUpcomingEvents] = useState<EventWithLocation[]>([]);
  const [pastEventStats, setPastEventStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRevenue, setShowRevenue] = useState(() => {
    const saved = localStorage.getItem('showRevenue');
    return saved !== null ? saved === 'true' : true;
  });

  const localeMap: Record<string, string> = { it: 'it-IT', en: 'en-GB', es: 'es-ES', fr: 'fr-FR', de: 'de-DE' };
  const dateLocale = localeMap[language] || 'it-IT';

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\./g, '/');
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem('showRevenue', String(showRevenue));
  }, [showRevenue]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load data with cache
      const [eventsData, totals, eventStatistics] = await Promise.all([
        withCache('homepage_events', () => eventApi.getAll(), 30000), // 30 seconds cache
        withCache('homepage_stats', () => statisticsApi.getTotalStatistics(), 30000),
        withCache('homepage_event_stats', () => statisticsApi.getEventStatistics(), 30000)
      ]);

      // Load locations to get images
      const { data: locations } = await supabase
        .from('locations')
        .select('name, image_url, image_position_x, image_position_y');

      const locationImagesMap = new Map(
        locations?.map(loc => [loc.name.toUpperCase().trim(), {
          url: loc.image_url,
          positionX: loc.image_position_x ?? 50,
          positionY: loc.image_position_y ?? 50
        }]) || []
      );

      // Get upcoming events (sort by date and take the next events)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to midnight for date comparison

      // Show maximum 4 upcoming events
      const upcoming = eventsData
        .filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= today;
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(0, 4)
        .map(event => {
          const locationData = locationImagesMap.get(event.location.toUpperCase().trim());
          return {
            ...event,
            locationImage: locationData?.url || null,
            locationImagePositionX: locationData?.positionX ?? 50,
            locationImagePositionY: locationData?.positionY ?? 50
          };
        });

      const statsMap = new Map(
        eventStatistics
          .filter(stat => stat.event_id || stat.id)
          .map(stat => [stat.event_id || stat.id, stat])
      );

      const pastStats = eventsData
        .filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate < today;
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 4)
        .map(event => {
          const stat = statsMap.get(event.id);
          return {
            id: stat?.id || event.id,
            event_id: event.id,
            men_count: stat?.men_count || 0,
            women_count: stat?.women_count || 0,
            total_participants: stat?.total_participants || 0,
            match_count: stat?.match_count || 0,
            men_revenue: stat?.men_revenue || 0,
            women_revenue: stat?.women_revenue || 0,
            total_revenue: stat?.total_revenue || 0,
            event: stat?.event || {
              id: event.id,
              name: event.name,
              date: event.date,
              time: event.time,
              location: event.location,
              city: event.city,
              age_range: event.age_range,
            },
          };
        });

      setUpcomingEvents(upcoming);
      setPastEventStats(pastStats);

      setStats({
        events: eventsData.length,
        participants: totals.total_participants || 0,
        locations: totals.locations_count || 0,
        matches: totals.match_count || 0,
        contacts: totals.contacts_count || 0,
        totalRevenue: totals.total_revenue || 0
      });
    } catch (err: any) {
      console.error('Error loading homepage data:', err);
      setError(err.message || t.common.error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t.homepage.title}</h1>
          <p className="text-gray-600">{t.homepage.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/30 overflow-hidden">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white/80 text-xs sm:text-sm font-medium truncate">{t.homepage.stats.totalEvents}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{stats.events}</p>
                </div>
                <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/30 overflow-hidden">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white/80 text-xs sm:text-sm font-medium truncate">{t.homepage.stats.participants}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{stats.participants}</p>
                </div>
                <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/30 overflow-hidden">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white/80 text-xs sm:text-sm font-medium truncate">{t.homepage.stats.locations}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{stats.locations}</p>
                </div>
                <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/30 overflow-hidden">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white/80 text-xs sm:text-sm font-medium truncate">{t.homepage.stats.matches}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{stats.matches}</p>
                </div>
                <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/30 overflow-hidden">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white/80 text-xs sm:text-sm font-medium truncate">{t.homepage.stats.totalClients}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{stats.contacts}</p>
                </div>
                <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/30 overflow-hidden">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white/80 text-xs sm:text-sm font-medium truncate">{t.homepage.stats.totalRevenue}</p>
                    <button
                      onClick={() => setShowRevenue(!showRevenue)}
                      className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                      title={showRevenue ? t.homepage.stats.hideRevenue : t.homepage.stats.showRevenue}
                    >
                      {showRevenue ? (
                        <Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white/80" />
                      ) : (
                        <EyeOff className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white/80" />
                      )}
                    </button>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1 truncate">
                    {showRevenue ? `${stats.totalRevenue.toFixed(2)} €` : t.common.hiddenAmount}
                  </p>
                </div>
                <div className="bg-white/20 p-2 sm:p-3 rounded-lg flex-shrink-0">
                  <Euro className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {pastEventStats.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">{t.homepage.recentEvents}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.eventName}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.ageRange}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.date}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.location}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.city}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.participants}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.matches}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.menRevenue}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.womenRevenue}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">{t.homepage.table.total}</th>
                  </tr>
                </thead>
                <tbody>
                  {pastEventStats.map((stat) => (
                    <tr key={stat.id} className="border-b border-gray-100">
                      <td className="py-3 px-4">{stat.event?.name || '-'}</td>
                      <td className="py-3 px-4">{stat.event?.age_range || '-'}</td>
                      <td className="py-3 px-4">{stat.event?.date ? formatDate(stat.event.date) : '-'}</td>
                      <td className="py-3 px-4">{stat.event?.location || '-'}</td>
                      <td className="py-3 px-4">{stat.event?.city || '-'}</td>
                      <td className="py-3 px-4">
                        <span className="text-blue-600">{t.homepage.table.men}{stat.men_count || 0}</span>
                        {' '}
                        <span className="text-rose-600">{t.homepage.table.women}{stat.women_count || 0}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-1">
                          <Heart className="h-4 w-4 text-rose-500 fill-rose-500" />
                          <span>{stat.match_count || 0}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-blue-600">
                        {showRevenue
                          ? (stat.men_revenue ? `${stat.men_revenue.toFixed(2)} €` : '0,00 €')
                          : t.common.hiddenAmount
                        }
                      </td>
                      <td className="py-3 px-4 text-rose-600">
                        {showRevenue
                          ? (stat.women_revenue ? `${stat.women_revenue.toFixed(2)} €` : '0,00 €')
                          : t.common.hiddenAmount
                        }
                      </td>
                      <td className="py-3 px-4 font-semibold">
                        {showRevenue
                          ? (stat.total_revenue ? `${stat.total_revenue.toFixed(2)} €` : '0,00 €')
                          : t.common.hiddenAmount
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-6">{t.homepage.upcomingEvents}</h2>
          {upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {upcomingEvents.map((event) => {
                const participantCount = event.participant_count || 0;
                const maxParticipants = event.max_participants;
                const percentageFull = (participantCount / maxParticipants) * 100;

                let ribbonText = '';
                let ribbonColor = '';

                if (event.status === 'sold-out + lista attesa') {
                  ribbonText = t.homepage.eventStatus.soldOut;
                  ribbonColor = 'bg-red-600';
                } else if (event.status === 'quasi completo') {
                  ribbonText = t.homepage.eventStatus.almostFull;
                  ribbonColor = 'bg-orange-500';
                } else if (event.status === 'posti limitati') {
                  ribbonText = t.homepage.eventStatus.limitedSpots;
                  ribbonColor = 'bg-yellow-500';
                } else if (event.status === 'in programma') {
                  ribbonText = t.homepage.eventStatus.scheduled;
                  ribbonColor = 'bg-green-600';
                }

                return (
                  <div key={event.id} className="relative bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                    {ribbonText && (
                      <div className="absolute top-0 right-0 z-10 overflow-hidden w-40 h-40 pointer-events-none">
                        <div className={`absolute top-8 ${event.status === 'sold-out + lista attesa' ? '-right-14' : event.status === 'in programma' || event.status === 'posti limitati' ? '-right-10' : '-right-11'} ${ribbonColor} text-white ${event.status === 'sold-out + lista attesa' ? 'text-[0.55rem]' : 'text-[0.65rem]'} font-bold ${event.status === 'sold-out + lista attesa' ? 'px-16' : event.status === 'in programma' || event.status === 'posti limitati' ? 'px-12' : 'px-11'} py-1.5 rotate-45 shadow-md whitespace-nowrap`}>
                          {ribbonText}
                        </div>
                      </div>
                    )}

                    <div className="aspect-video bg-gradient-to-br from-rose-500 to-pink-600 relative">
                      {((event as any).image_url || event.locationImage) ? (
                        <img
                          src={(event as any).image_url || event.locationImage}
                          alt={event.location}
                          className="w-full h-full object-cover"
                          style={!(event as any).image_url && event.locationImage ? {
                            objectPosition: `${event.locationImagePositionX ?? 50}% ${event.locationImagePositionY ?? 50}%`
                          } : undefined}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Calendar className="h-20 w-20 text-white/30" />
                        </div>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg text-gray-900 line-clamp-1 flex-1">{event.name}</h3>
                          <div className="flex items-center gap-1.5 text-gray-600 flex-shrink-0">
                            <Users className="h-4 w-4" />
                            <span className="text-sm whitespace-nowrap">{event.age_range}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 text-gray-600">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{formatDate(event.date)}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{formatTime(event.time)}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span className="text-sm truncate">{event.location} - {event.city}</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{t.homepage.stats.participants}</span>
                          <span className="font-semibold text-gray-900">
                            {participantCount} / {maxParticipants}
                          </span>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              percentageFull >= 100
                                ? 'bg-red-500'
                                : percentageFull >= 80
                                ? 'bg-orange-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(percentageFull, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center text-gray-500">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">{t.homepage.noEvents}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Homepage;